/*
 * gifted. — variant-aware materials line
 * PATH: assets/gifted-materials-variant.js
 *
 * Companion to blocks/gifted-materials.liquid (v3).
 *
 * WHAT THIS DOES, AND WHAT IT DELIBERATELY DOES NOT
 *   Liquid already renders the correct materials line for the variant the page
 *   loads on. This file does one job: update that line when the buyer changes
 *   variant. It reimplements nothing. It reads no prices, touches no form, and
 *   never writes to the cart.
 *
 *   If this file fails to load, fails to parse, or the event never fires, the
 *   page keeps the server-rendered line. That line is correct for the variant
 *   the page loaded on. A materials disclosure is never blank and never a
 *   placeholder. That is the whole failure design.
 *
 * THE EVENT
 *   shopify:product:select — a documented standard storefront event.
 *   Verified 11 Aug 2026 in Atelier 4.1.4: @shopify/events is bundled,
 *   assets/variant-picker.js dispatches ProductSelectEvent, and five of the
 *   theme's own components already listen for it (local-pickup, media-gallery,
 *   product-card, product-form, product-inventory).
 *
 *   The listen target convention is copied from those components verbatim:
 *     this.closest('.shopify-section, dialog')
 *   The picker dispatches on itself and the event reaches the section.
 *
 *   We use event.selectedOptions, which is available synchronously. We do NOT
 *   await event.promise — we need the chosen option value, not the resolved
 *   variant, so there is nothing to wait for and no flash of stale text.
 *
 * CUSTOM ELEMENT, NOT A DOMContentLoaded HANDLER
 *   Horizon morphs sections on variant change. connectedCallback re-runs after
 *   a morph; a one-shot listener would be silently lost.
 */

if (!customElements.get('gifted-materials')) {
  class GiftedMaterials extends HTMLElement {
    #controller = null;
    #config = null;

    connectedCallback() {
      this.#config = this.#readConfig();
      if (!this.#config || !this.#config.optionName) return;

      this.#controller = new AbortController();
      const target = this.closest('.shopify-section, dialog');
      target?.addEventListener('shopify:product:select', this.#handleSelect, {
        signal: this.#controller.signal,
      });
    }

    disconnectedCallback() {
      this.#controller?.abort();
      this.#controller = null;
    }

    /** Parse the JSON the block emitted. Any problem disables the upgrade. */
    #readConfig() {
      const node = this.querySelector('script[data-gifted-materials-map]');
      if (!node?.textContent) return null;
      try {
        const parsed = JSON.parse(node.textContent);
        if (!parsed || !Array.isArray(parsed.rules)) return null;
        return parsed;
      } catch {
        // Deliberately silent for the buyer. The server-rendered line stands.
        return null;
      }
    }

    #handleSelect = (event) => {
      const selected = event?.selectedOptions;
      if (!Array.isArray(selected)) return;

      const wanted = this.#config.optionName.trim().toLowerCase();
      const hit = selected.find(
        (opt) => String(opt?.name ?? '').trim().toLowerCase() === wanted
      );
      if (!hit) return;

      const value = String(hit.value ?? '').trim().toLowerCase();
      const rule = this.#config.rules.find(
        (r) => String(r.key ?? '').trim().toLowerCase() === value
      );

      // Unmapped value falls back to the product-level line, exactly as Liquid would.
      const raw = rule?.line || this.#config.unionLine || '';
      const line = this.#normalise(raw);
      if (!line) return;

      this.#render(line);
    };

    /** "A,  B , C" -> "A, B, C". Order is preserved: chain first, then charm. */
    #normalise(value) {
      return String(value)
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean)
        .join(', ');
    }

    #render(line) {
      const el = this.querySelector('[data-gifted-materials-metals]');
      if (el && el.textContent !== line) el.textContent = line;

      // Explainers follow the line that is actually showing.
      this.querySelectorAll('[data-gifted-materials-note]').forEach((note) => {
        const material = note.getAttribute('data-gifted-materials-note') || '';
        note.hidden = material ? !line.includes(material) : false;
      });
    }
  }

  customElements.define('gifted-materials', GiftedMaterials);
}
